import { 
    CssValueDefinition, 
    RegisterValuesArg, 
    GetValidClassArgs,
    DefaultValuesDictionary,
    PropertyDictionary
} from "../models/css-properties";

// Constant for the prefix key in property dictionary
const PREFIX = "prefix";

/**
 * Service for managing CSS class properties for widgets
 */
class CssPropertiesService {
    /**
     * Predefined theme color values
     */
    readonly themeColorValues: CssValueDefinition[] = [
        'base', 'primary', 'secondary', 'tertiary', 'inherit', 
        'info', 'success', 'warning', 'error', 'inverse'
    ];

    /**
     * Predefined fill mode values
     */
    readonly fillModeValues: CssValueDefinition[] = ['solid', 'outline', 'flat'];

    /**
     * Predefined shape values
     */
    readonly shapeValues: CssValueDefinition[] = ['rectangle', 'square'];

    /**
     * Predefined size values
     */
    readonly sizeValues: CssValueDefinition[] = [
        ['xsmall', 'xs'], ['small', 'sm'], ['medium', 'md'], ['large', 'lg']
    ];

    /**
     * Predefined rounded values
     */
    readonly roundedValues: CssValueDefinition[] = [
        ['small', 'sm'], ['medium', 'md'], ['large', 'lg'], ['full', 'full'], ['none', 'none']
    ];

    /**
     * Predefined position mode values
     */
    readonly positionModeValues: CssValueDefinition[] = [
        'fixed', 'static', 'sticky', 'absolute'
    ];

    /**
     * Predefined resize values
     */
    readonly resizeValues: CssValueDefinition[] = [
        ['both', 'resize'], ['horizontal', 'resize-x'], ['vertical', 'resize-y']
    ];

    /**
     * Predefined overflow values
     */
    readonly overflowValues: CssValueDefinition[] = [
        'auto', 'hidden', 'visible', 'scroll', 'clip'
    ];

    /**
     * Predefined layout flow values
     */
    readonly layoutFlowValues: CssValueDefinition[] = [
        ['vertical', '!k-flex-col'], ['horizontal', '!k-flex-row']
    ];

    /**
     * Default values dictionary
     */
    defaultValues: DefaultValuesDictionary = {};

    /**
     * Widget property dictionary
     */
    propertyDictionary: PropertyDictionary = {};

    /**
     * Legacy property to CSS class map (for BottomNavigation compatibility)
     * TODO: delete after implementing new styles and classes for BottomNavigation
     */
    propertyToCssClassMap: Record<string, Record<string, string>> = {};

    constructor() {
        // Register default CSS classes
        this.registerDefaultCssClasses("themeColor", this.themeColorValues);
        this.registerDefaultCssClasses("fillMode", this.fillModeValues);
        this.registerDefaultCssClasses("shape", this.shapeValues);
        this.registerDefaultCssClasses("size", this.sizeValues);
        this.registerDefaultCssClasses("positionMode", this.positionModeValues);
        this.registerDefaultCssClasses("rounded", this.roundedValues);
        this.registerDefaultCssClasses("resize", this.resizeValues);
        this.registerDefaultCssClasses("overflow", this.overflowValues);
        this.registerDefaultCssClasses("layoutFlow", this.layoutFlowValues);

        // Register legacy CSS classes (for BottomNavigation compatibility)
        this.registerCssClasses("themeColor", this.themeColorValues);
        this.registerCssClasses("fill", this.fillModeValues);
        this.registerCssClasses("shape", this.shapeValues);
        this.registerCssClasses("size", this.sizeValues);
        this.registerCssClasses("positionMode", this.positionModeValues);
    }

    /**
     * Register a CSS prefix for a widget
     */
    registerPrefix(widget: string, prefix: string): void {
        if (!this.propertyDictionary[widget]) {
            this.propertyDictionary[widget] = {};
        }

        this.propertyDictionary[widget][PREFIX] = prefix;
    }

    /**
     * Register CSS values for a widget
     */
    registerValues(widget: string, args: RegisterValuesArg[]): void {
        let i: number;
        let j: number;
        let prop: string;
        let values: Record<string, string>;
        let newValues: CssValueDefinition[];
        let currentValue: CssValueDefinition;

        if (!this.propertyDictionary[widget]) {
            this.propertyDictionary[widget] = {};
        }

        for (i = 0; i < args.length; i++) {
            prop = args[i].prop;
            newValues = args[i].values;

            if (!this.propertyDictionary[widget][prop]) {
                this.propertyDictionary[widget][prop] = {};
            }

            values = this.propertyDictionary[widget][prop] as Record<string, string>;

            for (j = 0; j < newValues.length; j++) {
                currentValue = newValues[j];

                if (Array.isArray(currentValue)) {
                    values[(currentValue as [string, string])[0]] = (currentValue as [string, string])[1];
                } else {
                    values[currentValue as string] = currentValue as string;
                }
            }
        }
    }

    /**
     * Get a valid CSS class for a widget property
     */
    getValidClass(args: GetValidClassArgs): string {
        const widget = args.widget;
        const propName = args.propName;
        const value = args.value;
        const overridePrefix = args.prefix;

        const defaultVals = this.defaultValues[propName];
        const widgetProperties = this.propertyDictionary[widget];

        if (!widgetProperties) {
            return "";
        }

        const widgetValues = widgetProperties[propName] as Record<string, string>;
        const validValue = widgetValues 
            ? widgetValues[value]
            : (defaultVals && defaultVals[value]);

        if (validValue) {
            let prefix: string;

            if (propName === "themeColor") {
                prefix = widgetProperties[PREFIX] as string;
            } else if (propName === "positionMode") {
                prefix = "k-pos-";
            } else if (propName === "rounded") {
                prefix = "k-rounded-";
            } else if (propName === "resize") {
                prefix = "k-";
            } else if (propName === "overflow") {
                prefix = "k-overflow-";
            } else if (propName === "layoutFlow") {
                prefix = "";
            } else {
                prefix = widgetProperties[PREFIX] as string;
            }

            prefix = overridePrefix || prefix;

            return prefix + validValue;
        } else {
            return "";
        }
    }

    /**
     * Register a single CSS class (internal for defaultValues)
     */
    private registerDefaultCssClass(propName: string, value: string, shorthand?: string): void {
        if (!this.defaultValues[propName]) {
            this.defaultValues[propName] = {};
        }

        this.defaultValues[propName][value] = shorthand || value;
    }

    /**
     * Register multiple CSS classes for a property (internal for defaultValues)
     */
    private registerDefaultCssClasses(propName: string, arr: CssValueDefinition[]): void {
        for (let i = 0; i < arr.length; i++) {
            if (Array.isArray(arr[i])) {
                const tuple = arr[i] as [string, string];
                this.registerDefaultCssClass(propName, tuple[0], tuple[1]);
            } else {
                this.registerDefaultCssClass(propName, arr[i] as string);
            }
        }
    }

    // Legacy CSS class methods (for BottomNavigation compatibility)
    // TODO: delete after implementing new styles and classes for BottomNavigation

    /**
     * Register a single legacy CSS class
     */
    registerCssClass(propName: string, value: string, shorthand?: string): void {
        if (!this.propertyToCssClassMap[propName]) {
            this.propertyToCssClassMap[propName] = {};
        }

        this.propertyToCssClassMap[propName][value] = shorthand || value;
    }

    /**
     * Register multiple legacy CSS classes
     */
    registerCssClasses(propName: string, arr: CssValueDefinition[]): void {
        for (let i = 0; i < arr.length; i++) {
            if (Array.isArray(arr[i])) {
                const tuple = arr[i] as [string, string];
                this.registerCssClass(propName, tuple[0], tuple[1]);
            } else {
                this.registerCssClass(propName, arr[i] as string);
            }
        }
    }

    /**
     * Get a valid legacy CSS class
     */
    getValidCssClass(prefix: string, propName: string, value: string): string | undefined {
        if (value === undefined) {
            return "";
        }

        const validValue = this.propertyToCssClassMap[propName]?.[value];

        if (validValue) {
            return prefix + validValue;
        }

        return undefined;
    }
}

export const cssPropertiesService = new CssPropertiesService();